'use client';

import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import '@splidejs/react-splide/css';
import type { GfMaterialItem } from '../../types';
import MaterialDetailImage from '../MaterialDetailImage/MaterialDetailImage';
import styles from './MaterialDetailSlider.module.scss';

export type MaterialDetailSliderRef = {
  goPrev: () => void;
  goNext: () => void;
};

type MaterialDetailSliderProps = {
  items: GfMaterialItem[];
  /** (再)マウント時の Splide 開始位置。マウント後の送りは Splide 内部が管理するので、この値の変化は無視される */
  startIndex: number;
  /** スワイプ/矢印で表示スライドが変わったとき（移動開始時に発火） */
  onIndexChange: (index: number) => void;
  /** ドラッグ開始/終了。モーダル背景クリックの誤発火抑止に使う */
  onDraggingChange: (isDragging: boolean) => void;
  /** 画像タップで拡大表示 */
  onImageClick: () => void;
};

/**
 * コマ詳細モーダルの画像スワイプ面。
 * スライドは一覧データのサムネで即描画する（差し替えなし）。
 * NEWバッジ・お気に入りは画像からはみ出して置くため、スライド内に入れるとトラックの端で見切れる。
 * 表示中コマぶんを親が画像の外側に重ねて描画する。
 * 利用可能商品などの詳細はスライダー外で親が index 変更に応じて遅延取得する。
 * スワイプは Splide の drag に委譲し、SP 標準スワイプとの軸干渉は Splide 側が処理する。
 * 矢印は親が ref 経由で操作する。
 */
const MaterialDetailSlider = forwardRef<MaterialDetailSliderRef, MaterialDetailSliderProps>(
  function MaterialDetailSlider({ items, startIndex, onIndexChange, onDraggingChange, onImageClick }, ref) {
    const splideRef = useRef<Splide>(null);
    // 開始位置はマウント時に確定させる。送りの途中で start が変わって位置が巻き戻るのを防ぐ。
    const startIndexRef = useRef(startIndex);

    useImperativeHandle(
      ref,
      () => ({
        goPrev: () => splideRef.current?.go('<'),
        goNext: () => splideRef.current?.go('>'),
      }),
      [],
    );

    // options の参照が毎レンダー変わると Splide が再初期化され送りアニメが途切れるため固定する
    const splideOptions = useMemo(
      () => ({
        type: 'slide',
        start: startIndexRef.current,
        perPage: 1,
        perMove: 1,
        arrows: false,
        pagination: false,
        drag: true,
      }),
      [],
    );

    const handleMove = (_splide: unknown, index: number) => {
      onIndexChange(index);
    };

    // ドラッグ終了フラグ戻しの遅延タイマー。閉じた直後にアンマウント後の state 更新を起こさないよう保持して片付ける。
    const dragEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleDragged = () => {
      // ドラッグ終了と同時にフラグを戻すと mouseup で背景クリックが発火するため、少し遅延させる
      if (dragEndTimerRef.current) clearTimeout(dragEndTimerRef.current);
      dragEndTimerRef.current = setTimeout(() => onDraggingChange(false), 50);
    };

    useEffect(() => {
      return () => {
        if (dragEndTimerRef.current) clearTimeout(dragEndTimerRef.current);
      };
    }, []);

    return (
      <Splide
        options={splideOptions}
        ref={splideRef}
        onMove={handleMove}
        onDrag={() => onDraggingChange(true)}
        onDragged={handleDragged}
        aria-label='コマ詳細'
        hasTrack={false}
        className={styles.splide}
      >
        <SplideTrack>
          {items.map((item, index) => (
            <SplideSlide key={item.id}>
              <MaterialDetailImage
                src={item.thumbnailPath}
                alt={item.name}
                onImageClick={onImageClick}
                priority={index === startIndexRef.current}
              />
            </SplideSlide>
          ))}
        </SplideTrack>
      </Splide>
    );
  },
);

export default MaterialDetailSlider;
